import { createContext, useCallback, useContext, useRef, useState } from "react";
import { EmailAuthProvider, reauthenticateWithCredential, signInWithCustomToken } from "firebase/auth";
import { ShieldCheck, Lock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { auth } from "../lib/firebase";
import { startSocialPulseStepUp, SOCIALPULSE_SSO_READY } from "../lib/socialpulse";
import api from "../lib/api";

/* Step-Up Protection — sensitive (Level-4) actions require a login within the last 10 minutes.
   Usage: const guard = useStepUp(); guard(() => doSensitiveThing());
   Wrap the app section with <StepUpProvider>. */

const StepUpContext = createContext(() => {});

export const useStepUp = () => useContext(StepUpContext);

export function StepUpProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = useRef(null);

  const guard = useCallback(async (action) => {
    try {
      const r = await api.get("/privacy/step-up-status");
      if (r.data.fresh) { action(); return; }
    } catch { /* fall through to modal */ }
    pending.current = action;
    setOpen(true);
  }, []);

  const hasPassword = !!auth?.currentUser?.providerData?.some((p) => p.providerId === "password");

  const finish = async () => {
    const t = await auth.currentUser.getIdToken(true); // refreshed token carries new auth_time
    localStorage.setItem("token", t);
    setOpen(false); setPassword("");
    toast.success("Identity confirmed");
    const act = pending.current; pending.current = null;
    if (act) setTimeout(act, 50);
  };

  const withPassword = async (e) => {
    e?.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      await reauthenticateWithCredential(auth.currentUser,
        EmailAuthProvider.credential(auth.currentUser.email, password));
      await finish();
    } catch { toast.error("Incorrect password — please try again"); }
    setBusy(false);
  };

  const withSocialPulse = async () => {
    setBusy(true);
    try {
      const customToken = await startSocialPulseStepUp(); // popup → fresh custom token
      await signInWithCustomToken(auth, customToken);     // same uid, new auth_time
      await finish();
    } catch (e) { toast.error(e.message || "Re-verification failed"); }
    setBusy(false);
  };

  return (
    <StepUpContext.Provider value={guard}>
      {children}
      {open && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" data-testid="stepup-modal"
          onMouseDown={(e) => { if (e.target === e.currentTarget) { setOpen(false); pending.current = null; } }}>
          <div className="bg-white w-full max-w-sm p-6 border-t-4" style={{ borderColor: "#EA4335" }}>
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-5 h-5" style={{ color: "#EA4335" }} />
              <h3 className="font-heading font-900 text-lg">Confirm it's you</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4 flex items-start gap-1.5">
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              This is a sensitive document action. For your protection, please sign in again (required every 10 minutes).
            </p>
            {hasPassword ? (
              <form onSubmit={withPassword} className="space-y-3">
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus
                  placeholder="Your password" data-testid="stepup-password"
                  className="w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-red-400" />
                <button type="submit" disabled={busy || !password} data-testid="stepup-submit"
                  className="w-full py-2.5 text-white text-sm font-700 disabled:opacity-50" style={{ background: "#EA4335" }}>
                  {busy ? "Verifying…" : "Confirm identity"}
                </button>
              </form>
            ) : (
              <button onClick={withSocialPulse} disabled={busy || !SOCIALPULSE_SSO_READY} data-testid="stepup-socialpulse"
                className="w-full py-2.5 text-white text-sm font-700 disabled:opacity-50 inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700">
                <Sparkles className="w-4 h-4" /> {busy ? "Waiting for SocialPulse…" : "Re-verify with SocialPulse"}
              </button>
            )}
            <button onClick={() => { setOpen(false); pending.current = null; }} data-testid="stepup-cancel"
              className="w-full mt-2 py-2 text-sm text-muted-foreground hover:text-zinc-800">Cancel</button>
          </div>
        </div>
      )}
    </StepUpContext.Provider>
  );
}
